import { Modal, Button, Upload, message, Typography, notification, UploadFile, Radio } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import React, { useState } from 'react';
import { ref, uploadBytes } from 'firebase/storage';
import { storage } from '../../config/firebase-config'; // adjust import path
import { useAppDispatch, useAppSelector, useAuth } from '../../store/hooks';
import { parseCustomProductsUpload } from '../../api/parseCustomProductsUpload';
import { fetchCustomProducts } from '../../store/customProductSlice';

const { Dragger } = Upload;

interface UploadCustomProductModalProps {
    open: boolean;
    onClose: () => void;
}

type UploadMode = 'default' | 'akeneo' | 'pimcore';

const isExcelFile = (file: File) =>
    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    file.type === 'application/vnd.ms-excel' ||
    file.name.endsWith('.xlsx') ||
    file.name.endsWith('.xls');

const isJsonFile = (file: File) =>
    file.type === 'application/json' ||
    file.name.endsWith('.json');

const parseServerResponseMessage = (value: unknown): string | undefined => {
    if (typeof value !== 'string') return undefined;
    try {
        const parsed = JSON.parse(value) as { message?: unknown };
        return typeof parsed.message === 'string' ? parsed.message : undefined;
    } catch {
        return undefined;
    }
};

const getUploadErrorDescription = (err: unknown): string => {
    const fallback = 'There was an error uploading the file. If the error persists please contact support';
    if (!err || typeof err !== 'object') return fallback;

    const details = (err as { details?: unknown }).details;
    if (details && typeof details === 'object') {
        const detailMessage = (details as { message?: unknown }).message;
        if (typeof detailMessage === 'string' && detailMessage.trim()) {
            return detailMessage;
        }
    }

    const serverResponse = (err as { customData?: { _serverResponse?: unknown } }).customData?._serverResponse;
    const serverMessage = parseServerResponseMessage(serverResponse);
    if (serverMessage) return serverMessage;

    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
        return message;
    }

    return fallback;
};

const UploadCustomProductsModal: React.FC<UploadCustomProductModalProps> = ({
    open,
    onClose,
}) => {
    const displatch = useAppDispatch()
    const [file, setFile] = useState<File | null>(null);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [loading, setLoading] = useState(false);
    const auth = useAuth();
    const selectedCompany = useAppSelector((state) => state.companies.selectedCompany)
    const [uploadMode, setUploadMode] = useState<UploadMode>('default');

    const beforeUpload = (file: File) => {
        const isValidFile = uploadMode === 'pimcore' ? isJsonFile(file) : isExcelFile(file);

        if (!isValidFile) {
            message.error(
                uploadMode === 'pimcore'
                    ? 'Only JSON files (.json) are allowed for this upload mode'
                    : 'Only Excel files (.xlsx, .xls) are allowed',
            );
            return Upload.LIST_IGNORE;
        }

        setFile(file);
        setFileList([file as any]); // AntD expects UploadFile type, so cast is okay here
        return false;
    };

    const handleUpload = async () => {
        if (!file) return;
        setLoading(true);
        try {
            if (!selectedCompany) throw Error("No company selected for uploading")
            if (!auth) throw Error("No user logged in")

            const storageRef = ref(storage, `custom-product-uploads/${auth?.currentUser?.uid}/${selectedCompany?.id}/${file.name}`);

            await uploadBytes(storageRef, file);
            // Trigger backend parsing with selected upload mode
            await parseCustomProductsUpload(
                storageRef.fullPath,
                storageRef.bucket,
                selectedCompany.id,
                uploadMode,
            );
            displatch(fetchCustomProducts({
                companyId: selectedCompany.id,
                pageSize: 10,
                lastVisibleDoc: null,
                activeSorter: null,
                activeFilters: {},
                refetch: true
            }))

            notification.success({
                message: 'Upload Successful',
                description: 'The file has been uploaded successfully.',
            });
        } catch (err) {
            console.error('Upload failed', err);
            notification.error({
                message: 'Upload Failed',
                description: getUploadErrorDescription(err),
                duration: 0,
            });
        } finally {
            setFile(null);
            setFileList([]);
            setLoading(false);
            onClose();
        }
    };

    return (
        <Modal
            open={open}
            title="Upload Amazon Product File"
            width="600px"
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <Button key="upload" type="primary" onClick={handleUpload} disabled={!file} loading={loading}>
                    Upload
                </Button>,
            ]}
        >
            <Typography.Paragraph>
                Select the <strong>Upload Mode</strong>:
            </Typography.Paragraph>
            <Radio.Group
                value={uploadMode}
                onChange={(e) => {
                    setUploadMode(e.target.value);
                    setFile(null);
                    setFileList([]);
                }}
                style={{ marginBottom: 16 }}
            >
                <Radio.Button value="default">Default Upload File</Radio.Button>
                <Radio.Button value="akeneo" style={{ marginLeft: 8 }}>
                    Akeneo Upload File
                </Radio.Button>
                <Radio.Button value="pimcore" style={{ marginLeft: 8 }}>
                    Structured Catalog Upload
                </Radio.Button>
            </Radio.Group>
            <Typography.Paragraph>
                {uploadMode === 'pimcore'
                    ? (
                        <>
                            Please upload a <strong>.json</strong> file containing structured localized product data.
                        </>
                    )
                    : (
                        <>
                            Please upload a <strong>.xlsx</strong> file containing product data. Mandatory columns are highlighted in the template; Category, Subcategory, and Product Type are used for keyword research and assignment. Use marketplace and language codes such as <strong>de+DE</strong>, <strong>fr+FR</strong>, <strong>en+UK</strong>, <strong>es+ES</strong>, or <strong>nl+BE</strong>.
                        </>
                    )}
            </Typography.Paragraph>
            <Dragger
                beforeUpload={beforeUpload}
                maxCount={1}
                showUploadList={true}
                accept={uploadMode === 'pimcore' ? '.json' : '.xlsx,.xls'}
                multiple={false}
                customRequest={() => false} // Prevent auto-upload
                onRemove={() => {
                    setFile(null);
                    setFileList([]);
                }}
                fileList={fileList}
                style={{ padding: 16, borderRadius: 8 }}
            >
                <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                    {uploadMode === 'pimcore'
                        ? 'Click or drag JSON file to this area to upload'
                        : 'Click or drag Excel file to this area to upload'}
                </p>
                <p className="ant-upload-hint">
                    {uploadMode === 'pimcore'
                        ? 'Only .json files are supported'
                        : 'Only .xlsx and .xls files are supported'}
                </p>
            </Dragger>
        </Modal>
    );
};

export default UploadCustomProductsModal;
